import { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy } from 'lucide-react';
import { useApi } from '@/lib/api';
import { ROLE_LABELS } from '@/lib/labels';
import { Button, Card, ErrorText, PageHeader } from '@/components/ui';
import { PosterChoices } from '@/components/poster-card';
import { useOrgContext } from './layout';

/**
 * Posters for one invite code: a QR code to the join link (and the code to
 * type), to print for notice boards or share online, plus a message to
 * paste into chat groups and email.
 */
export function OrgInvitePosterPage() {
  const { codeId = '' } = useParams();
  const { org, canManage } = useOrgContext();
  const api = useApi();
  const codes = useQuery({
    queryKey: ['inviteCodes', org.id],
    queryFn: () => api.inviteCodes(org.id),
    enabled: canManage,
  });
  if (!canManage) return <Navigate to="../.." replace />;
  if (codes.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (codes.isError) return <ErrorText error={codes.error} />;
  const code = codes.data.codes.find((c) => c.id === codeId);
  if (!code)
    return (
      <Card>
        <p className="text-sm text-muted-foreground">
          This invite code was revoked or doesn&apos;t exist. Create a new one in Invite members.
        </p>
      </Card>
    );

  const link = `${window.location.origin}/join/${code.code}`;
  return (
    <div className="space-y-6">
      <PageHeader
        title="Invite poster"
        description={`Invite people to join ${org.name} with code ${code.code} (joins as ${ROLE_LABELS[code.role].toLowerCase()}). Print it for notice boards, classrooms and break rooms, or share the dark version in chat groups, newsletters and email. Anyone who scans it joins, so share it only where you mean to.`}
        back={{ to: '..', label: 'Invite members' }}
      />
      <PosterChoices
        source={{
          key: ['invite', org.id, code.id],
          file: (theme, format, size) => api.invitePosterFile(org.id, code.id, theme, format, size),
          fileName: (theme, d) =>
            `Nirvik-Ready-Invite-${code.code}-${theme}${d.format === 'pdf' ? `-${d.size ?? 'letter'}` : ''}`,
        }}
      />
      <ShareMessage
        text={`You're invited to join ${org.name} on Nirvik Ready! It's free: short courses and challenges that help you spot scams, protect your accounts and keep up with AI-era threats, at your own pace on any device. Join in under a minute: ${link} (or enter code ${code.code} at ${window.location.host}/join).`}
      />
    </div>
  );
}

function ShareMessage({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Card title="Message to send with it">
      <p className="text-sm text-muted-foreground">
        For WhatsApp, Slack, Teams or email. Edit it to suit your organization.
      </p>
      <p className="mt-3 rounded-md border border-border bg-muted/50 p-3 text-sm">{text}</p>
      <Button
        variant="secondary"
        className="mt-3 w-full sm:w-auto"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          } catch {
            // Clipboard blocked; the text is visible to copy by hand.
          }
        }}
      >
        {copied ? (
          <Check className="size-4" aria-hidden />
        ) : (
          <Copy className="size-4" aria-hidden />
        )}
        {copied ? 'Copied' : 'Copy message'}
      </Button>
    </Card>
  );
}
